import memoize from 'lodash.memoize';
import { createSelector, createStructuredSelector } from 'reselect';
import * as technical from './iiif/technical';
import * as descriptive from './iiif/descriptive';
import { getAllAnnotationLists, getAllCanvases, getAllExternalResources, getAllImages, getAllLayers, getAllRanges, getAllSequences, getAllServices } from '../all';
import * as linking from './iiif/linking';
import * as structural from './iiif/structural';

var manifest = memoize(function (selector) {
  /**************************************************
   * Technical properties
   *
   * - getId
   * - getType
   * - getViewingHint
   * - getViewingDirection
   * - getNavDate
   **************************************************/
  var getId = createSelector(selector, technical.getId);

  var getType = createSelector(selector, technical.getType);

  var getViewingHint = createSelector(selector, technical.getWhitelistedViewingHint(['individuals', 'paged', 'continuous']));

  var getViewingDirection = createSelector(selector, technical.getViewingDirection);

  var getNavDate = createSelector(selector, technical.getNavDate);

  /**************************************************
   * Descriptive properties
   *
   * - getLabel
   * - getDescription
   * - getMetadata
   * - getAttribution
   * - getLogo
   * - getLicense
   * - getThumbnail
   **************************************************/
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getDescription = createSelector(selector, descriptive.getDescription);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getAttribution = createSelector(selector, descriptive.getAttribution);
  var getLogo = createSelector(selector, descriptive.getLogo);

  var getLicense = createSelector(selector, descriptive.getLicense);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);
  var getThumbnail = createSelector(getThumbnailId, getAllImages, function (thumbnailId, allImages) {
    return allImages[thumbnailId] || thumbnailId;
  });

  /**************************************************
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getRelated
   * - getRendering
   * - getWithin
   **************************************************/
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllExternalResources, function (seeAlsoIds, allExternalResources) {
    return seeAlsoIds.map(function (seeAlsoId) {
      return allExternalResources[seeAlsoId];
    });
  });

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, function (serviceIds, allServices) {
    return serviceIds.map(function (serviceId) {
      return allServices[serviceId];
    });
  });

  var getRelatedIds = createSelector(selector, linking.getRelated);
  var getRelated = createSelector(getRelatedIds, getAllExternalResources, function (relatedIds, allExternalResources) {
    return relatedIds.map(function (relatedId) {
      return allExternalResources[relatedId];
    });
  });

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllExternalResources, function (renderingIds, allExternalResources) {
    return renderingIds.map(function (renderingId) {
      return allExternalResources[renderingId];
    });
  });

  var getWithinIds = createSelector(selector, linking.getWithin);
  var getWithin = createSelector(getWithinIds, getAllLayers, getAllExternalResources, function (withinIds, allLayers, allExternalResources) {
    return withinIds.map(function (within) {
      return allLayers[within.id] || allExternalResources[within.id];
    }).filter(function (e) {
      return e;
    });
  });

  /**************************************************
   * Structural properties
   *
   * - getSequences (Required)
   * - getStructures / getRanges
   **************************************************/
  var getSequenceIds = createSelector(selector, structural.getSequences);
  var getSequences = createSelector(getSequenceIds, getAllSequences, function (sequenceIds, allSequences) {
    return sequenceIds.map(function (sequenceId) {
      return allSequences[sequenceId];
    });
  });

  var getRangeIds = createSelector(selector, structural.getRanges);
  var getRanges = createSelector(getRangeIds, getAllRanges, function (rangeIds, allRanges) {
    return rangeIds.map(function (rangeId) {
      return allRanges[rangeId];
    });
  });

  var getStructureIds = getRangeIds;
  var getStructures = getRanges;

  var getOtherContentIds = createSelector(selector, structural.getOtherContent);
  var getOtherContent = createSelector(getOtherContentIds, getAllExternalResources, getAllAnnotationLists, function (otherContentIds, allExternalResources, allAnnotationLists) {
    return otherContentIds.map(function (otherContentId) {
      return allAnnotationLists[otherContentId] || allExternalResources[otherContentId] || {
        '@id': otherContentId,
        label: [{ '@value': 'unknown', '@language': '@none' }]
      };
    });
  });

  /**
   * Presentation 3 compatibility layer.
   */
  var getCanvasIds = createSelector(getSequences, function (sequences) {
    return sequences.reduce(function (sequenceCanvasList, nextSequence) {
      return [].concat(sequenceCanvasList, structural.getCanvases(nextSequence));
    }, []);
  });
  var getCanvases = createSelector(getCanvasIds, getAllCanvases, function (canvasIds, allCanvases) {
    return canvasIds.map(function (canvasId) {
      return allCanvases[canvasId];
    });
  });

  return {
    selector: selector,
    // Technical
    getId: getId,
    getType: getType,
    getViewingHint: getViewingHint,
    getViewingDirection: getViewingDirection,
    getNavDate: getNavDate,
    // Descriptive
    getLabel: getLabel,
    getDescription: getDescription,
    getMetadata: getMetadata,
    getAttribution: getAttribution,
    getLogo: getLogo,
    getLicense: getLicense,
    getThumbnailId: getThumbnailId,
    getThumbnail: getThumbnail,
    // Linking
    getWithinIds: getWithinIds,
    getWithin: getWithin,
    getRenderingIds: getRenderingIds,
    getRendering: getRendering,
    getRelatedIds: getRelatedIds,
    getRelated: getRelated,
    getServiceIds: getServiceIds,
    getService: getService,
    getSeeAlsoIds: getSeeAlsoIds,
    getSeeAlso: getSeeAlso,
    // Structural
    getSequenceIds: getSequenceIds,
    getSequences: getSequences,
    getRangeIds: getRangeIds,
    getRanges: getRanges,
    getStructureIds: getStructureIds,
    getStructures: getStructures,
    getOtherContentIds: getOtherContentIds,
    getOtherContent: getOtherContent,
    getCanvasIds: getCanvasIds,
    getCanvases: getCanvases
  };
});

export default manifest;